/**
 * BaseWealthicaConnect Component
 * 
 * Unified connection logic for all Wealthica connection components.
 * This component consolidates connection handling while preserving
 * all existing functionality and interfaces.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { wealthica2Client } from '../client';
import { getRedirectUri } from '../utils/environment';
import Spinner from '@/components/ui/Spinner';
import { useAuthStore } from '@/stores/authStore';
import { toast } from 'sonner';
import { useWealthica2Navigation } from '../context/navigation';
import { connectionGuard } from '../utils/connection-guard';
import { logger } from '../utils/logger';
import { clearCallbackDedupFlags } from '../utils/navigation-flags';

interface BaseWealthicaConnectProps {
  /**
   * Display variant for the component
   */
  variant?: 'button' | 'card' | 'inline';
  
  /**
   * Provider name to connect to
   */
  provider?: string;
  
  /**
   * Reference ID for the connection
   */
  reference?: string;
  
  /**
   * Custom redirect URI
   */
  redirectUri?: string;
  
  /**
   * Success callback
   */
  onSuccess?: () => void;
  
  /**
   * Connect callback (called before connection starts)
   */
  onConnect?: () => void;
  
  // Button-specific props
  buttonVariant?: 'default' | 'outline' | 'secondary' | 'ghost' | 'link' | 'destructive';
  buttonSize?: 'default' | 'sm' | 'lg' | 'icon';
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  
  // Timeout configuration
  timeoutMs?: number;
  
  // Error boundary reset callback
  onReset?: () => void;
}

/**
 * Hook for managing connection parameters
 */
function useConnectionParams(props: BaseWealthicaConnectProps, user: any) {
  return useCallback(() => {
    const params: any = {};
    
    // Handle different parameter patterns for backward compatibility
    if (props.provider) {
      // ConnectButton pattern: { provider }
      params.provider = props.provider;
    }
    
    if (props.redirectUri || props.reference) {
      // Wealthica2Connect pattern: { redirectUri, reference }
      params.redirectUri = props.redirectUri || getRedirectUri();
      params.reference = props.reference || user?.id;
    } else if (user?.id) {
      // ConnectInvestments pattern: { reference: user.id }
      params.reference = user.id;
    }
    
    return params;
  }, [props.provider, props.redirectUri, props.reference, user?.id]);
}

/**
 * Hook for managing connection state and logic
 */
function useWealthicaConnection(props: BaseWealthicaConnectProps) {
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const [connectTimeout, setConnectTimeout] = useState<NodeJS.Timeout | null>(null);
  const { user } = useAuthStore();
  const navigation = useWealthica2Navigation();
  const getConnectionParams = useConnectionParams(props, user);
  
  const clearConnectTimeout = useCallback(() => {
    if (connectTimeout) {
      clearTimeout(connectTimeout);
      setConnectTimeout(null);
    }
  }, [connectTimeout]);
  
  const handleConnect = useCallback(async () => {
    // Prevent duplicate connection attempts
    if (isConnecting || (user?.id && connectionGuard.isConnecting(user.id))) {
      logger.debug('BaseWealthicaConnect', 'Already connecting, ignoring duplicate request');
      return;
    }
    
    if (!user) {
      toast.error('You must be logged in to connect your Wealthica account');
      return;
    }
    
    // Use connection guard to prevent race conditions
    if (user.id && !connectionGuard.startConnection(user.id, props.timeoutMs || 30000)) {
      logger.debug('BaseWealthicaConnect', 'Connection guard prevented duplicate attempt');
      return;
    }
    
    // Ensure previous callback de-dup flags are cleared before a fresh connect
    try {
      clearCallbackDedupFlags();
    } catch (e) {
      logger.warn('BaseWealthicaConnect', 'Failed to clear callback dedup flags', {
        error: e instanceof Error ? e.message : String(e)
      });
    }

    logger.info('BaseWealthicaConnect', 'Starting connection process');
    setIsConnecting(true);
    
    // Call onConnect callback before starting connection
    if (props.onConnect) {
      props.onConnect();
    }
    
    // Set timeout to prevent infinite spinner
    const timeout = setTimeout(() => {
      logger.warn('BaseWealthicaConnect', 'Connection timeout reached');
      setIsConnecting(false);
      if (user.id) {
        connectionGuard.endConnection(user.id);
      }
      
      // Simple user notification with retry option
      toast.error('Connection is taking longer than expected. Please try again.', {
        duration: 6000,
        action: {
          label: 'Retry',
          onClick: () => handleConnect()
        }
      });
    }, props.timeoutMs || 30000);
    setConnectTimeout(timeout);
    
    try {
      const connectionParams = getConnectionParams();
      logger.debug('BaseWealthicaConnect', 'Calling wealthica2Client.connect', { 
        params: connectionParams 
      });
      
      // Connect to Wealthica and get redirect info
      const result = await wealthica2Client.connect(connectionParams);
      
      logger.debug('BaseWealthicaConnect', 'Connect result', { 
        success: result.success, 
        hasRedirectUrl: !!result.redirectUrl 
      });
      
      if (result.success && result.redirectUrl) {
        // Save state parameter to NavigationProvider for CSRF protection
        if (result.state) {
          navigation.saveState(result.state);
          logger.debug('BaseWealthicaConnect', 'Saved state parameter for CSRF protection');
        }
        
        // Save provider if specified (ConnectButton behavior)
        if (props.provider) {
          navigation.saveProvider(props.provider);
          navigation.saveCurrentLocation(props.provider);
        }
        
        // Call success callback
        if (props.onSuccess) {
          props.onSuccess();
        }
        
        logger.info('BaseWealthicaConnect', 'Redirecting to Wealthica', { 
          redirectUrl: result.redirectUrl 
        });
        
        // Clear timeout and connection guard before redirect
        clearConnectTimeout();
        if (user.id) {
          connectionGuard.endConnection(user.id);
        }
        
        // Redirect to Wealthica
        window.location.href = result.redirectUrl;
      } else {
        throw new Error(result.error || 'Failed to generate connection URL');
      }
    } catch (error) {
      logger.error('BaseWealthicaConnect', 'Connection failed', { error });
      clearConnectTimeout();
      if (user.id) {
        connectionGuard.endConnection(user.id);
      }
      
      const errorMessage = props.variant === 'button' 
        ? 'Could not initiate connection' 
        : 'Failed to connect to Wealthica. Please try again.';
      
      toast.error(errorMessage);
      setIsConnecting(false);
    }
  }, [
    isConnecting, 
    user, 
    props.onConnect, 
    props.onSuccess, 
    props.timeoutMs, 
    props.variant,
    props.provider,
    getConnectionParams, 
    navigation, 
    clearConnectTimeout
  ]);
  
  // Cleanup timeout and connection guard on unmount
  useEffect(() => {
    return () => {
      clearConnectTimeout();
      if (user?.id) {
        connectionGuard.endConnection(user.id);
      }
    };
  }, [user?.id, clearConnectTimeout]);
  
  // Reset function for error boundaries
  const handleReset = useCallback(() => {
    setIsConnecting(false);
    clearConnectTimeout();
    if (user?.id) {
      connectionGuard.endConnection(user.id);
    }
    if (props.onReset) {
      props.onReset();
    }
  }, [user?.id, clearConnectTimeout, props.onReset]);
  
  return {
    isConnecting,
    handleConnect,
    handleReset
  };
}

/**
 * BaseWealthicaConnect Component
 * 
 * Provides unified connection logic that can be used by all Wealthica
 * connection components while preserving their existing interfaces.
 */
export const BaseWealthicaConnect: React.FC<BaseWealthicaConnectProps> = (props) => {
  const { isConnecting, handleConnect, handleReset } = useWealthicaConnection(props);
  
  // Button variant rendering
  if (props.variant === 'button' || !props.variant) {
    return (
      <Button
        variant={props.buttonVariant || 'default'}
        size={props.buttonSize || 'default'}
        onClick={handleConnect}
        disabled={isConnecting || props.disabled}
        className={props.className}
      >
        {isConnecting ? (
          <>
            <Spinner size="xs" variant="muted" className="mr-2" ariaLabel="Connecting" />
            Connecting...
          </>
        ) : (
          props.children || 'Connect Wealthica'
        )}
      </Button>
    );
  }
  
  // Card variant rendering (Wealthica2Connect style)
  if (props.variant === 'card') {
    return (
      <div className="flex flex-col gap-4">
        <h3 className="text-lg font-semibold">Connect to Wealthica</h3>
        <p className="text-sm text-gray-500">
          Connect your Wealthica account to view your investments.
        </p>
        <Button
          onClick={handleConnect}
          className="w-full sm:w-auto"
          disabled={isConnecting || props.disabled}
        >
          {isConnecting ? (
            <>
              <Spinner size="xs" variant="muted" className="mr-2" ariaLabel="Connecting" />
              Connecting...
            </>
          ) : (
            'Connect Wealthica'
          )}
        </Button>
      </div>
    );
  }
  
  // Inline variant rendering (for inline usage like ConnectInvestments)
  if (props.variant === 'inline') {
    return (
      <Button
        className={props.className || "w-full group mt-auto"}
        onClick={handleConnect}
        disabled={isConnecting || props.disabled}
      >
        {isConnecting ? 'Connecting...' : (props.children || 'Connect Account')}
      </Button>
    );
  }
  
  // Default fallback
  return null;
};

export default BaseWealthicaConnect;